import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Send, ShieldCheck } from 'lucide-react'
import { explain } from '@/shared/api/problem'
import { OffsetPager } from '@/shared/ui/CursorPager'
import { ErrorStrip, LoadingBlock } from '@/shared/ui/states'
import { Panel, SectionHeader } from '@/components/erp/primitives'
import { can as hasPermission, useSession } from '@/shared/auth/session'
import { Pill, fmtRelative, label } from '@/features/dossier/components/primitives'
import { approveDraft, editDraft, listAccounts, listQueue, rejectDraft,
         type Account, type Draft } from './api'
import { DraftAsset } from './DraftAsset'

/**
 * The approval queue — the one screen the Centaur model actually runs through.
 *
 * ## Why the queue is a screen and not a notification
 *
 * Because approving is the job. Every other page in this feature can be read passively; this
 * one is a work list, and a person sitting at it is what stands between a model's sentence and
 * a platform. Rejections need a note for the same reason approvals need a name: "we decided not
 * to answer" is a decision, and a decision without a reason cannot be reviewed later.
 *
 * ## No bulk approve
 *
 * Deliberately absent. A button that sent nine replies with one click would be automation
 * wearing a human's name, which is the exact failure the whole design exists to prevent. Each
 * draft is read, and each one is approved on its own.
 */
export function QueuePage() {
  const authorities = useSession((s) => s.authorities)
  const canDeploy = hasPermission(authorities, 'warroom:deploy')
  const canDraft = hasPermission(authorities, 'warroom:draft')

  const [drafts, setDrafts] = useState<Draft[] | null>(null)
  const [accounts, setAccounts] = useState<Account[]>([])
  const [accountId, setAccountId] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [editing, setEditing] = useState<string | null>(null)
  const [editBody, setEditBody] = useState('')
  const [rejecting, setRejecting] = useState<string | null>(null)
  const [note, setNote] = useState('')
  const [query, setQuery] = useState('')
  const [draftQuery, setDraftQuery] = useState('')
  const [page, setPage] = useState(0)
  const [total, setTotal] = useState({ pages: 1, elements: 0 })

  const load = useCallback(async () => {
    try {
      setError(null)
      const found = await listQueue({ q: query || undefined, page, size: 50 })
      setDrafts(found.content)
      setTotal({ pages: found.totalPages, elements: found.totalElements })
    } catch (err) {
      setError(explain(err))
    }
  }, [query, page])

  useEffect(() => { void load() }, [load])
  useEffect(() => {
    void listAccounts().then((rows) => {
      setAccounts(rows)
      setAccountId((c) => c || rows.find((a) => a.active)?.id || '')
    }).catch(() => setAccounts([]))
  }, [])

  async function run(id: string, work: () => Promise<unknown>) {
    setBusy(id)
    setError(null)
    try {
      await work()
      await load()
    } catch (err) {
      setError(explain(err))
    } finally {
      setBusy(null)
    }
  }

  if (error && !drafts) return <ErrorStrip message={error} />
  if (!drafts) return <LoadingBlock label="Loading the queue" />

  const postable = accounts.filter((a) => a.active)

  return (
    <div className="space-y-3">
      <SectionHeader
        title="Awaiting a human"
        description="Nothing here has been sent. Each one needs a person to read it, put their
                     name on it, and choose the account it goes out from."
      />

      {error && <ErrorStrip message={error} />}

      <SearchBar draft={draftQuery} onDraft={setDraftQuery} query={query}
                 onSearch={() => { setPage(0); setQuery(draftQuery) }}
                 onClear={() => { setDraftQuery(''); setQuery(''); setPage(0) }}
                 total={total.elements} unit="drafts" />

      <Panel>
        <div className="flex flex-wrap items-center gap-2 p-2">
          <ShieldCheck className="size-3.5 text-emerald-600" />
          <p className="text-[11px] text-muted-foreground">
            {drafts.length === 0
              ? 'The queue is empty. '
              : `${drafts.length} draft${drafts.length === 1 ? '' : 's'} waiting. `}
            Approval records your user id against the exact text sent — the draft can be edited
            after, and the ledger will still hold what actually went out.
          </p>
          {postable.length === 0 && (
            <span className="text-[11px] text-amber-700 dark:text-amber-400">
              No posting account is configured, so nothing can be approved yet.
            </span>
          )}
        </div>
      </Panel>

      {drafts.map((d) => (
        <Panel key={d.id}>
          <div className="space-y-1.5 p-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex flex-wrap items-center gap-1.5">
                <Pill className="border-border">{label(d.tacticCode)}</Pill>
                {d.personaName && (
                  <Pill className="border-sky-500/30 bg-sky-500/10 text-sky-700
                                   dark:text-sky-400">{d.personaName}</Pill>
                )}
                <span className="text-[10px] text-muted-foreground">
                  {d.advisorRunId ? 'drafted by the council' : 'written by a person'} ·{' '}
                  {fmtRelative(d.createdAt)}
                </span>
              </div>
              <Link to={`/war-room/threads/${d.threadId}`}
                    className="text-[11px] text-primary underline">
                Read it in the thread
              </Link>
            </div>

            {editing === d.id ? (
              <div className="space-y-1">
                <textarea value={editBody} rows={3}
                          onChange={(e) => setEditBody(e.target.value)}
                          className="w-full rounded border border-input bg-background px-2 py-1
                                     text-[12px]" />
                <div className="flex gap-1.5">
                  <button type="button" disabled={busy === d.id}
                          onClick={() => void run(d.id, async () => {
                            await editDraft(d.id, editBody)
                            setEditing(null)
                          })}
                          className="rounded bg-primary px-2 py-0.5 text-[10px]
                                     text-primary-foreground">Save</button>
                  <button type="button" onClick={() => setEditing(null)}
                          className="rounded border border-border px-2 py-0.5 text-[10px]">
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <p className="whitespace-pre-wrap rounded border border-border bg-muted/20 p-2
                            text-[12px]">{d.body}</p>
            )}

            {d.assetId && <DraftAsset assetId={d.assetId} />}

            <p className="text-[10px] text-muted-foreground">{d.rationale}</p>
            {d.editedFrom && (
              <details>
                <summary className="cursor-pointer text-[10px] text-muted-foreground">
                  What the council originally wrote
                </summary>
                <p className="mt-0.5 whitespace-pre-wrap text-[10px] text-muted-foreground">
                  {d.editedFrom}
                </p>
              </details>
            )}

            <div className="flex flex-wrap items-end gap-1.5 pt-1">
              {canDraft && (
                <>
                  <button type="button"
                          onClick={() => { setEditing(d.id); setEditBody(d.body) }}
                          className="rounded border border-border px-2 py-0.5 text-[11px]">
                    Edit before sending
                  </button>
                  <button type="button"
                          onClick={() => setRejecting(rejecting === d.id ? null : d.id)}
                          className="rounded border border-border px-2 py-0.5 text-[11px]">
                    Do not send
                  </button>
                </>
              )}
              {canDeploy && (
                <>
                  <select value={accountId} onChange={(e) => setAccountId(e.target.value)}
                          className="h-7 rounded border border-input bg-background px-2
                                     text-[11px]">
                    {postable.length === 0 && <option value="">no account</option>}
                    {postable.map((a) => (
                      <option key={a.id} value={a.id}>
                        @{a.handle} ({a.postedToday}/{a.dailyCap} today)
                      </option>
                    ))}
                  </select>
                  <button type="button" disabled={busy === d.id || !accountId}
                          onClick={() => void run(d.id, () => approveDraft(d.id, accountId))}
                          className="inline-flex items-center gap-1 rounded bg-primary px-3
                                     py-1 text-[11px] text-primary-foreground
                                     disabled:opacity-50">
                    <Send className="size-3" /> Approve &amp; deploy
                  </button>
                </>
              )}
            </div>

            {rejecting === d.id && (
              <div className="flex flex-wrap items-end gap-1.5">
                <input value={note} onChange={(e) => setNote(e.target.value)}
                       placeholder="Why not? At least 10 characters — it stays on the draft."
                       className="h-7 flex-1 rounded border border-input bg-background px-2
                                  text-[11px]" />
                <button type="button"
                        disabled={busy === d.id || note.trim().length < 10}
                        onClick={() => void run(d.id, async () => {
                          await rejectDraft(d.id, note.trim())
                          setRejecting(null); setNote('')
                        })}
                        className="rounded border border-rose-500/40 px-2 py-1 text-[11px]
                                   text-rose-700 disabled:opacity-50 dark:text-rose-400">
                  Reject it
                </button>
              </div>
            )}
          </div>
        </Panel>
      ))}

      <OffsetPager page={page} totalPages={total.pages} totalElements={total.elements}
                   onPage={setPage} loading={false} unit="drafts" />
    </div>
  )
}

/**
 * Search and paging for one of the war room's unbounded lists.
 *
 * <p>Threads, the approval queue, the ledger and coordination signals all accumulate for the
 * life of a campaign. The bounded lists — accounts, voices, watches, platforms — are
 * deliberately NOT paged: a room has a handful of each, and a pager that never has anything to
 * do is a control that teaches people to ignore controls.</p>
 */
function SearchBar({
  draft, onDraft, query, onSearch, onClear, total, unit,
}: {
  draft: string
  onDraft: (v: string) => void
  query: string
  onSearch: () => void
  onClear: () => void
  total: number
  unit: string
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <input value={draft} onChange={(e) => onDraft(e.target.value)}
             onKeyDown={(e) => { if (e.key === 'Enter') onSearch() }}
             placeholder="Search the draft or its rationale…"
             className="h-7 w-full max-w-xs rounded border border-input bg-background px-2
                        text-[12px]" />
      <button type="button" onClick={onSearch}
              className="rounded border border-border px-2 py-1 text-[11px]">Search</button>
      {query && (
        <button type="button" onClick={onClear}
                className="rounded border border-border px-2 py-1 text-[11px]">Clear</button>
      )}
      <span className="text-[10px] text-muted-foreground">
        {total} {unit}
      </span>
    </div>
  )
}
